import { useEffect, useRef } from 'react'
import { AxiosError, InternalAxiosRequestConfig } from 'axios'
import Toast from 'react-native-toast-message'
import { secureTokenStore } from '@store'
import { api } from '@api'
import { getPlanLimitCode, getPlanLimitParams, isPlanLimitError } from '@utils'
import { isSessionExpiredError } from '../utils/sessionExpiry'
import { useAuth } from './AuthProvider'
import { useLanguage } from './LanguageContext'
import { usePaywall } from './PaywallProvider'

interface RefreshTokenResponse {
  accessToken: string
  refreshToken: string
}

const refreshTokens = async (currentRefreshToken: string): Promise<RefreshTokenResponse> => {
  const { default: axios } = await import('axios')
  const refreshApi = axios.create({
    baseURL: api.defaults.baseURL,
  })

  const { data } = await refreshApi.post<RefreshTokenResponse>(
    '/authentication/refresh-token',
    { refreshToken: currentRefreshToken },
  )

  await secureTokenStore.setTokens(data.accessToken, data.refreshToken)

  return data
}

// The server itself refuses the session (unknown, expired or malformed
// refresh token). Anything else (no answer, 429, 5xx) says nothing about the
// session, which may still be valid for weeks.
const isSessionRejected = (error: unknown) => {
  const status = (error as AxiosError)?.response?.status
  return status === 401 || status === 400
}

const RENEWAL_RETRY_DELAY_MS = 1500
const RENEWAL_PAUSE_MS = 10_000
// The API allows 10 renewals a minute: after a 429, wait for the full window.
const RENEWAL_PAUSE_AFTER_429_MS = 60_000

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export const SetupAxiosInterceptors = () => {
  const { signOut } = useAuth()
  const { t } = useLanguage()
  const { showPaywall } = usePaywall()
  const isRefreshing = useRef(false)
  // After a renewal that failed without the server refusing the session, no
  // new renewal is sent before this time.
  const refreshBlockedUntil = useRef(0)
  const failedQueue = useRef<Array<{
    resolve: (value?: any) => void
    reject: (error?: any) => void
    config: InternalAxiosRequestConfig
  }>>([])

  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use((config) => {
      if (
        config.url?.includes('/authentication/sign-in') ||
        config.url?.includes('/authentication/forgot-password') ||
        config.url?.includes('/authentication/reset-password') ||
        config.url?.includes('/authentication/refresh-token') ||
        config.url?.includes('/authentication/revoke-token')
      ) {
        return config
      }

      const token = secureTokenStore.getAccessToken()
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
      return config
    })

    const processQueue = (error: any, token: string | null = null) => {
      failedQueue.current.forEach((prom) => {
        if (error) {
          prom.reject(error)
        } else {
          if (token) {
            prom.config.headers['Authorization'] = `Bearer ${token}`
          }
          api.request(prom.config).then(prom.resolve).catch(prom.reject)
        }
      })
      failedQueue.current = []
    }

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        if (isSessionExpiredError(error) && originalRequest && !originalRequest._retry) {
          if (originalRequest.url?.includes('/authentication/sign-in')) {
            return Promise.reject(error)
          }

          // Renewal on pause: the caller gets its own error, the session is kept.
          if (Date.now() < refreshBlockedUntil.current) {
            return Promise.reject(error)
          }

          if (isRefreshing.current) {
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ resolve, reject, config: originalRequest })
            })
          }

          originalRequest._retry = true
          isRefreshing.current = true

          const currentRefreshToken = secureTokenStore.getRefreshToken()

          if (!currentRefreshToken) {
            isRefreshing.current = false
            signOut()
            return Promise.reject(error)
          }

          try {
            let response: RefreshTokenResponse
            try {
              response = await refreshTokens(currentRefreshToken)
            } catch (firstError) {
              // No answer or a server error: one second try a moment later.
              // Not after a 429, which would only feed the limit.
              const status = (firstError as AxiosError)?.response?.status
              if (status !== undefined && status < 500) throw firstError
              await wait(RENEWAL_RETRY_DELAY_MS)
              response = await refreshTokens(currentRefreshToken)
            }

            originalRequest.headers['Authorization'] = `Bearer ${response.accessToken}`
            processQueue(null, response.accessToken)
            isRefreshing.current = false
            return api.request(originalRequest)
          } catch (refreshError) {
            isRefreshing.current = false
            processQueue(refreshError, null)

            if (isSessionRejected(refreshError)) {
              signOut()
              Toast.show({ type: 'info', text2: t('errors.sessionExpired') })
              return Promise.reject(refreshError)
            }

            // A passing failure: keep the session and the tokens, and leave
            // the renewal alone for a while instead of signing the user out.
            const status = (refreshError as AxiosError)?.response?.status
            refreshBlockedUntil.current =
              Date.now() + (status === 429 ? RENEWAL_PAUSE_AFTER_429_MS : RENEWAL_PAUSE_MS)
            Toast.show({ type: 'info', text2: t('errors.sessionCheckFailed') })
            return Promise.reject(refreshError)
          }
        }

        // A free account went over a plan limit or used a Premium-only
        // feature: raise the paywall automatically, whichever screen made the
        // call. Callers still receive the rejection.
        if (isPlanLimitError(error)) {
          showPaywall({ code: getPlanLimitCode(error), params: getPlanLimitParams(error) })
        }

        return Promise.reject(error)
      },
    )

    return () => {
      api.interceptors.request.eject(requestInterceptor)
      api.interceptors.response.eject(responseInterceptor)
    }
  }, [signOut, showPaywall, t])

  return null
}
